import React, { useState } from "react";
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import type { BranchAction, OrderSnapshot } from "../flow/types";
import { stepById } from "../flow/flowGraph";

interface Props {
  actions: BranchAction[];
  snapshot?: OrderSnapshot;
  disabled: boolean;
  onRun: (stepId: string) => void;
}

const BranchActions: React.FC<Props> = ({ actions, snapshot, disabled, onRun }) => {
  const [confirming, setConfirming] = useState<string | null>(null);
  const stateLabel = snapshot?.child
    ? `${snapshot.child.orderStatus} / supplier ${snapshot.child.supplierStatus || "—"}`
    : snapshot?.enquiry
    ? snapshot.enquiry.orderStatus
    : "no order";

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="700" fontSize="sm">
          Branch actions
        </Text>
        <Text fontSize="10px" color="gray.400" fontFamily="mono">
          state: {stateLabel}
        </Text>
      </HStack>
      <Text fontSize="xs" color="gray.500" mb={3}>
        Only actions accepted by the backend state machines for the current state are shown. Each action executes, then verifies the resulting state.
      </Text>
      {!actions.length ? (
        <Text fontSize="xs" color="gray.400">
          {snapshot?.enquiry ? "No branch actions are valid from this state." : "Run the flow to an intermediate target to unlock branch actions."}
        </Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          {actions.map((a) => {
            const step = stepById(a.stepId);
            const isConfirming = confirming === a.id;
            return (
              <Box key={a.id} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2.5}>
                <HStack justify="space-between" align="flex-start">
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="600">
                      {a.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {a.description}
                    </Text>
                    {step && (
                      <Text fontSize="10px" color="gray.400" fontFamily="mono" mt={1}>
                        {step.actor} · {step.id}
                      </Text>
                    )}
                  </Box>
                  {isConfirming ? (
                    <HStack>
                      <Button size="xs" variant="ghost" onClick={() => setConfirming(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        colorScheme={a.tone === "danger" ? "red" : "blue"}
                        onClick={() => {
                          setConfirming(null);
                          onRun(a.stepId);
                        }}
                      >
                        Confirm
                      </Button>
                    </HStack>
                  ) : (
                    <Button
                      size="xs"
                      variant={a.tone === "primary" ? "solid" : "outline"}
                      colorScheme={a.tone === "danger" ? "red" : a.tone === "primary" ? "blue" : "gray"}
                      isDisabled={disabled}
                      onClick={() => (a.tone === "danger" ? setConfirming(a.id) : onRun(a.stepId))}
                    >
                      Execute
                    </Button>
                  )}
                </HStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default BranchActions;
